/**
 * Vendor → Lien Waivers tab (construction pack)
 * Shows all lien waivers for this vendor across all projects.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useConstructionPack } from "@/lib/useConstructionPack";
import { useQuery } from "@tanstack/react-query";
import { lienWaiversListQueryOptions } from "@/lib/queries";
import type { LienWaiver } from "@/lib/types";

export const Route = createFileRoute("/_auth/vendors/$vendorId/lien-waivers")({
  component: VendorLienWaiversTab,
});

const TYPE_LABEL: Record<string, string> = {
  conditional_partial: "Cond. Partial",
  unconditional_partial: "Uncond. Partial",
  conditional_final: "Cond. Final",
  unconditional_final: "Uncond. Final",
};

const STATUS_COLOR: Record<string, string> = {
  requested:
    "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300",
  received:
    "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
  verified:
    "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300",
  void: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
};

function fmt(n: string | number) {
  const v = parseFloat(String(n));
  return isNaN(v)
    ? "—"
    : v.toLocaleString("en-CA", { style: "currency", currency: "CAD", maximumFractionDigits: 0 });
}

function VendorLienWaiversTab() {
  const { enabled: constructionEnabled } = useConstructionPack();
  if (!constructionEnabled) return null;
  const { vendorId } = Route.useParams();
  const waiversQ = useQuery(lienWaiversListQueryOptions({ vendor_id: vendorId }));
  const waivers: LienWaiver[] = waiversQ.data ?? [];

  const verified = waivers.filter((w) => w.status === "verified").length;
  const pending = waivers.filter((w) => ["requested", "received"].includes(w.status)).length;

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
            Lien Waivers
          </h2>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            {waivers.length} total ·{" "}
            <span className="text-green-700 dark:text-green-400 font-medium">
              {verified} verified
            </span>{" "}
            ·{" "}
            {pending > 0 && (
              <span className="text-amber-700 dark:text-amber-400 font-medium">
                {pending} pending
              </span>
            )}
          </p>
        </div>
      </div>

      {waiversQ.isLoading && (
        <div className="text-sm text-slate-500 dark:text-slate-400">Loading…</div>
      )}
      {waiversQ.error && (
        <div className="text-sm text-red-600">
          Failed to load: {(waiversQ.error as Error).message}
        </div>
      )}

      {!waiversQ.isLoading && waivers.length === 0 && (
        <div className="text-sm text-slate-400 dark:text-slate-500">
          No lien waivers on file for this vendor.
        </div>
      )}

      {waivers.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-gray-800">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-gray-900 text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide">
              <tr>
                <th className="text-left px-3 py-2.5">Waiver #</th>
                <th className="text-left px-3 py-2.5">Type</th>
                <th className="text-left px-3 py-2.5">Status</th>
                <th className="text-left px-3 py-2.5">Through</th>
                <th className="text-right px-3 py-2.5">Amount</th>
                <th className="text-left px-3 py-2.5">Signed</th>
                <th className="text-left px-3 py-2.5">Verified</th>
                <th className="text-left px-3 py-2.5">Notes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-gray-800 bg-white dark:bg-gray-900">
              {waivers
                .slice()
                .sort(
                  (a, b) =>
                    new Date(b.through_date).getTime() -
                    new Date(a.through_date).getTime(),
                )
                .map((w) => (
                  <tr
                    key={w.id}
                    className="hover:bg-slate-50 dark:hover:bg-gray-800/50 transition-colors"
                  >
                    <td className="px-3 py-2.5 font-mono text-slate-800 dark:text-slate-200">
                      {w.waiver_no}
                    </td>
                    <td className="px-3 py-2.5 text-xs text-slate-600 dark:text-slate-400">
                      {TYPE_LABEL[w.waiver_type] ?? w.waiver_type}
                    </td>
                    <td className="px-3 py-2.5">
                      <span
                        className={`px-2 py-0.5 text-xs rounded font-medium ${STATUS_COLOR[w.status] ?? ""}`}
                      >
                        {w.status}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-slate-600 dark:text-slate-400 text-xs">
                      {w.through_date}
                    </td>
                    <td className="px-3 py-2.5 text-right font-medium text-slate-900 dark:text-slate-100">
                      {fmt(w.amount_through)}
                    </td>
                    <td className="px-3 py-2.5 text-xs text-slate-500 dark:text-slate-400">
                      {w.signed_date ?? "—"}
                    </td>
                    <td className="px-3 py-2.5 text-xs text-slate-500 dark:text-slate-400">
                      {w.verified_date ?? "—"}
                    </td>
                    <td className="px-3 py-2.5 text-xs text-slate-500 dark:text-slate-400 max-w-xs truncate">
                      {w.notes ?? "—"}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
